import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const CProblems = () => {
  const { id } = useParams(); 
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProblem, setSelectedProblem] = useState(null);

  useEffect(() => {
    if (selectedProblem) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedProblem]);

  const allLanguageData = {
    "1": { 
      "name": "C",
      "sections": [
        {
          "section": "SECTION 1 — Basic",
          "problems": [
            { 
              "title": "Print \"Hello World\"", 
              "desc": "The classic first program.",
              "detailedDesc": "Write a C program to print \"Hello World\" on the console. This introduces the basic structure of a C program.",
              "logic": "1. Include stdio.h\n2. Use printf to print text\n3. Return 0",
              "code": "#include <stdio.h>\n\nint main() {\n    printf(\"Hello World\\n\");\n    return 0;\n}",
              "output": "Hello World\n"
            },
            { 
              "title": "Print Name and Age", 
              "desc": "Print variables of different types.",
              "detailedDesc": "Declare a string for a name and an integer for an age. Assign values and print them.",
              "logic": "1. Include stdio.h\n2. Declare char array and int\n3. Use %s and %d format specifiers",
              "code": "#include <stdio.h>\n\nint main() {\n    char name[] = \"Sai\";\n    int age = 21;\n    printf(\"My name is %s and I am %d years old.\\n\", name, age);\n    return 0;\n}",
              "output": "My name is Sai and I am 21 years old.\n"
            },
            { 
              "title": "Add Two Numbers", 
              "desc": "Read two integers and print their sum.",
              "logic": "1. Declare two variables\n2. Assign values\n3. Add and print",
              "code": "#include <stdio.h>\n\nint main() {\n    int a = 10, b = 20;\n    int sum = a + b;\n    printf(\"Sum: %d\\n\", sum);\n    return 0;\n}",
              "output": "Sum: 30\n"
            },
            { 
              "title": "Multiply Two Floats", 
              "desc": "Read two floats and print their product.",
              "logic": "1. Declare float variables\n2. Multiply using *\n3. Print using %f",
              "code": "#include <stdio.h>\n\nint main() {\n    float num1 = 2.5, num2 = 4.0;\n    printf(\"Product: %.2f\\n\", num1 * num2);\n    return 0;\n}",
              "output": "Product: 10.00\n"
            },
            { 
              "title": "Find ASCII Value", 
              "desc": "Print the ASCII value of a character.",
              "logic": "1. Declare char\n2. Print char using %d format specifier",
              "code": "#include <stdio.h>\n\nint main() {\n    char c = 'A';\n    printf(\"ASCII value of %c is %d\\n\", c, c);\n    return 0;\n}",
              "output": "ASCII value of A is 65\n"
            },
            { 
              "title": "Swap Two Numbers", 
              "desc": "Swap variables using a temporary variable.",
              "logic": "1. Store a in temp\n2. Assign b to a\n3. Assign temp to b",
              "code": "#include <stdio.h>\n\nint main() {\n    int a = 5, b = 10, temp;\n    temp = a;\n    a = b;\n    b = temp;\n    printf(\"a = %d, b = %d\\n\", a, b);\n    return 0;\n}",
              "output": "a = 10, b = 5\n"
            },
            { 
              "title": "Size of Data Types", 
              "desc": "Find the size of int, float, double and char.",
              "logic": "1. Use sizeof() operator\n2. Print result in bytes",
              "code": "#include <stdio.h>\n\nint main() {\n    printf(\"Size of int: %lu bytes\\n\", sizeof(int));\n    printf(\"Size of char: %lu bytes\\n\", sizeof(char));\n    return 0;\n}",
              "output": "Size of int: 4 bytes\nSize of char: 1 bytes\n"
            },
            { 
              "title": "Simple Interest", 
              "desc": "Calculate simple interest using P*T*R/100.",
              "logic": "1. Declare P, T, R\n2. Apply formula SI = (P*T*R)/100",
              "code": "#include <stdio.h>\n\nint main() {\n    float p = 1000, r = 5, t = 2;\n    float si = (p * t * r) / 100;\n    printf(\"Simple Interest = %.2f\\n\", si);\n    return 0;\n}",
              "output": "Simple Interest = 100.00\n"
            },
            { 
              "title": "Area of a Circle", 
              "desc": "Calculate the area using pi * r * r.",
              "logic": "1. Define PI as 3.14159\n2. Calculate area",
              "code": "#include <stdio.h>\n#define PI 3.14159\n\nint main() {\n    float r = 5.0;\n    printf(\"Area: %.2f\\n\", PI * r * r);\n    return 0;\n}",
              "output": "Area: 78.54\n"
            },
            { 
              "title": "Fahrenheit to Celsius", 
              "desc": "Convert temperature formatting.",
              "logic": "1. Take Fahrenheit\n2. Apply C = (F - 32) * 5/9",
              "code": "#include <stdio.h>\n\nint main() {\n    float f = 98.6;\n    float c = (f - 32) * 5 / 9;\n    printf(\"Celsius: %.2f\\n\", c);\n    return 0;\n}",
              "output": "Celsius: 37.00\n"
            }
          ]
        },
        {
          "section": "SECTION 2 — Easy",
          "problems": [
            { 
              "title": "Even or Odd", 
              "desc": "Check if a number is even or odd.",
              "logic": "1. Use modulo operator %\n2. If num % 2 == 0, even else odd",
              "code": "#include <stdio.h>\n\nint main() {\n    int num = 7;\n    if(num % 2 == 0) printf(\"Even\\n\");\n    else printf(\"Odd\\n\");\n    return 0;\n}",
              "output": "Odd\n"
            },
            { 
              "title": "Vowel or Consonant", 
              "desc": "Check if an alphabet is a vowel or consonant.",
              "logic": "1. Check if char is a, e, i, o, u\n2. Handle uppercase and lowercase",
              "code": "#include <stdio.h>\n\nint main() {\n    char c = 'E';\n    if(c=='a'||c=='e'||c=='i'||c=='o'||c=='u'||c=='A'||c=='E'||c=='I'||c=='O'||c=='U')\n        printf(\"Vowel\\n\");\n    else\n        printf(\"Consonant\\n\");\n    return 0;\n}",
              "output": "Vowel\n"
            },
            { "title": "Largest of Three", "desc": "Find the largest number among three inputs.", "code": "#include <stdio.h>\nint main() {\n    int a=1,b=5,c=3;\n    if(a>=b && a>=c) printf(\"%d\", a);\n    else if(b>=a && b>=c) printf(\"%d\", b);\n    else printf(\"%d\", c);\n    return 0;\n}", "output": "5" },
            { "title": "Leap Year", "desc": "Check if a given year is a leap year.", "code": "#include <stdio.h>\nint main() {\n    int y=2024;\n    if((y%4==0 && y%100!=0) || (y%400==0)) printf(\"Leap\");\n    else printf(\"Not Leap\");\n    return 0;\n}", "output": "Not Leap" },
            { "title": "Factorial of a Number", "desc": "Find the factorial using a loop.", "code": "#include <stdio.h>\nint main() {\n    int n=5, fact=1;\n    for(int i=1; i<=n; i++) fact*=i;\n    printf(\"%d\", fact);\n    return 0;\n}", "output": "120" },
            { "title": "Fibonacci Series", "desc": "Print the Fibonacci sequence up to n terms.", "code": "// Code to print fibonacci...", "output": "0 1 1 2 3 5" },
            { "title": "Prime Number", "desc": "Check if a number is prime or not.", "code": "// Code to check prime...", "output": "Prime" },
            { "title": "Palindrome Number", "desc": "Check if a number reads the same forwards and backwards.", "code": "// Code to check palindrome...", "output": "Palindrome" },
            { "title": "Sum of Digits", "desc": "Calculate the sum of digits of an integer.", "code": "// Code for sum of digits...", "output": "Sum is 15" },
            { "title": "Armstrong Number", "desc": "Check if a number is an Armstrong number.", "code": "// Code for Armstrong...", "output": "Armstrong" }
          ]
        },
        {
          "section": "SECTION 3 — Medium",
          "problems": [
            { "title": "Array Sum", "desc": "Calculate the sum of all elements in an array.", "code": "#include <stdio.h>\nint main() {\n  int arr[] = {1,2,3,4,5}, sum = 0;\n  for(int i=0; i<5; i++) sum+=arr[i];\n  printf(\"%d\", sum);\n  return 0;\n}", "output": "15" },
            { "title": "Reverse an Array", "desc": "Reverse the elements of an array in place.", "code": "// Array reverse logic...", "output": "5 4 3 2 1" },
            { "title": "Linear Search", "desc": "Find an element in an array using linear search." },
            { "title": "Binary Search", "desc": "Implement binary search on a sorted array." },
            { "title": "Bubble Sort", "desc": "Sort an array using bubble sort algorithm." },
            { "title": "Matrix Addition", "desc": "Add two 2D matrices." },
            { "title": "String Length", "desc": "Find the length of a string without strlen()." },
            { "title": "String Concatenation", "desc": "Concatenate two strings manually." },
            { "title": "Pointers Basics", "desc": "Demonstrate the use of pointers and memory addresses." },
            { "title": "Call by Reference", "desc": "Swap two numbers using pointers." }
          ]
        },
        {
          "section": "SECTION 4 — Complex",
          "problems": [
            { "title": "Dynamic Memory", "desc": "Use malloc, calloc, realloc, and free.", "code": "// Malloc code here...", "output": "Memory allocated successfully." },
            { "title": "Singly Linked List", "desc": "Create and print a singly linked list." },
            { "title": "Stack using Array", "desc": "Implement Push and Pop operations." },
            { "title": "Queue using Array", "desc": "Implement Enqueue and Dequeue operations." },
            { "title": "File Handling Write", "desc": "Write text to a file using fprintf." },
            { "title": "File Handling Read", "desc": "Read text from a file using fscanf." },
            { "title": "Merge Sort", "desc": "Implement the merge sort algorithm." },
            { "title": "Quick Sort", "desc": "Implement the quick sort algorithm." },
            { "title": "Structs and Unions", "desc": "Compare memory allocation of structs vs unions." },
            { "title": "Command Line Args", "desc": "Pass arguments to main() and print them." }
          ]
        }
      ]
    },
    "2": { 
      "name": "C++",
      "sections": [
        {
          "section": "SECTION 1 — Basic",
          "problems": [
            { "title": "Hello C++", "desc": "Print Hello World using cout.", "code": "#include <iostream>\nusing namespace std;\nint main() {\n  cout << \"Hello World\";\n  return 0;\n}", "output": "Hello World" },
            { "title": "Basic I/O", "desc": "Read data using cin and print using cout." },
            { "title": "Arithmetic Operations", "desc": "Perform basic math operations." },
            { "title": "Data Types", "desc": "Understand int, float, double, string." },
            { "title": "Type Casting", "desc": "Convert float to int implicitly and explicitly." },
            { "title": "Swap Variables", "desc": "Swap two numbers without a temp variable." },
            { "title": "Simple Calculator", "desc": "Use switch case to build a basic calculator." },
            { "title": "Even or Odd", "desc": "Check if a number is divisible by 2." },
            { "title": "Largest Number", "desc": "Find the largest of 3 numbers." },
            { "title": "Vowel or Consonant", "desc": "Use conditional statements for alphabet check." }
          ]
        },
        {
          "section": "SECTION 2 — Easy",
          "problems": [
            { "title": "For Loop Basics", "desc": "Print numbers from 1 to 10." },
            { "title": "While Loop Basics", "desc": "Sum of natural numbers using while." },
            { "title": "Pattern Printing", "desc": "Print a right-angled triangle pattern." },
            { "title": "Factorial", "desc": "Find factorial of a number." },
            { "title": "Prime Check", "desc": "Verify if a number is prime." },
            { "title": "Fibonacci Series", "desc": "Generate series up to N." },
            { "title": "Reverse String", "desc": "Reverse a string using built-in functions." },
            { "title": "Palindrome Check", "desc": "Check if a string is a palindrome." },
            { "title": "Min/Max Array", "desc": "Find min and max elements in an array." },
            { "title": "Function Basics", "desc": "Create a void function and call it." }
          ]
        },
        {
          "section": "SECTION 3 — Medium",
          "problems": [
            { "title": "Class and Object", "desc": "Define a simple OOP class." },
            { "title": "Constructors", "desc": "Implement default and parameterized constructors." },
            { "title": "Function Overloading", "desc": "Create multiple functions with the same name." },
            { "title": "Inheritance", "desc": "Implement single inheritance." },
            { "title": "Polymorphism", "desc": "Use virtual functions for runtime polymorphism." },
            { "title": "Pointers and Arrays", "desc": "Iterate an array using pointers." },
            { "title": "Dynamic Arrays", "desc": "Allocate memory using 'new' keyword." },
            { "title": "Recursion", "desc": "Calculate factorial using recursion." },
            { "title": "String Methods", "desc": "Use append, length, and substr." },
            { "title": "Exception Handling", "desc": "Use try, catch, and throw blocks." }
          ]
        },
        {
          "section": "SECTION 4 — Complex",
          "problems": [
            { "title": "Vectors", "desc": "Perform CRUD operations on std::vector." },
            { "title": "Maps", "desc": "Use std::map for key-value pairs." },
            { "title": "Sets", "desc": "Find unique elements using std::set." },
            { "title": "Templates", "desc": "Create a generic template function." },
            { "title": "File Streams", "desc": "Read and write data using fstream." },
            { "title": "Linked List Ops", "desc": "Build an OOP based singly linked list." },
            { "title": "Stack using STL", "desc": "Solve a problem using std::stack." },
            { "title": "Queue using STL", "desc": "Process elements using std::queue." },
            { "title": "Graph BFS", "desc": "Implement Breadth-First Search." },
            { "title": "Graph DFS", "desc": "Implement Depth-First Search." }
          ]
        }
      ]
    },
    "3": { 
      "name": "Java",
      "sections": [
        {
          "section": "SECTION 1 — Basic",
          "problems": [
            { "title": "Hello World", "desc": "Basic public static void main syntax.", "code": "public class Main {\n  public static void main(String[] args) {\n    System.out.println(\"Hello World\");\n  }\n}", "output": "Hello World" },
            { "title": "Variables", "desc": "Declare and initialize variables." },
            { "title": "Data Types", "desc": "Understand primitives vs objects." },
            { "title": "User Input", "desc": "Take input using the Scanner class." },
            { "title": "Operators", "desc": "Practice arithmetic and logical operators." },
            { "title": "Type Casting", "desc": "Widening and narrowing casting." },
            { "title": "Swap Numbers", "desc": "Swap values with a third variable." },
            { "title": "Math Class", "desc": "Use Math.max and Math.sqrt." },
            { "title": "Even or Odd", "desc": "Use modulo operator." },
            { "title": "Max of Two", "desc": "Use ternary operator to find max." }
          ]
        },
        {
          "section": "SECTION 2 — Easy",
          "problems": [
            { "title": "Control Flow", "desc": "If-else conditions practice." },
            { "title": "For Loop", "desc": "Print multiplication tables." },
            { "title": "While Loop", "desc": "Sum of digits." },
            { "title": "Factorial", "desc": "Find factorial of N." },
            { "title": "Prime Check", "desc": "Optimized prime number logic." },
            { "title": "Fibonacci", "desc": "Print Fibonacci sequence." },
            { "title": "Array Basics", "desc": "Initialize and print an array." },
            { "title": "String Basics", "desc": "String length and charAt." },
            { "title": "StringBuilder", "desc": "Reverse a string efficiently." },
            { "title": "Palindrome", "desc": "Check string palindrome." }
          ]
        },
        {
          "section": "SECTION 3 — Medium",
          "problems": [
            { "title": "Classes & Objects", "desc": "Create a Car class and instantiate it." },
            { "title": "Methods", "desc": "Pass by value concepts." },
            { "title": "Constructors", "desc": "Constructor overloading." },
            { "title": "Inheritance", "desc": "Use 'extends' keyword." },
            { "title": "Polymorphism", "desc": "Method overriding (@Override)." },
            { "title": "Encapsulation", "desc": "Private fields and Getters/Setters." },
            { "title": "Abstraction", "desc": "Abstract classes and methods." },
            { "title": "Interfaces", "desc": "Implement multiple interfaces." },
            { "title": "Exception Handling", "desc": "Try, catch, finally blocks." },
            { "title": "ArrayList", "desc": "Dynamic array operations." }
          ]
        },
        {
          "section": "SECTION 4 — Complex",
          "problems": [
            { "title": "HashMap", "desc": "Count character frequencies." },
            { "title": "HashSet", "desc": "Remove duplicates from a list." },
            { "title": "Multi-threading", "desc": "Extend Thread class and run." },
            { "title": "Runnable Interface", "desc": "Implement Runnable for threads." },
            { "title": "File I/O", "desc": "Read/Write using BufferedReader/Writer." },
            { "title": "Lambda Expressions", "desc": "Functional programming basics." },
            { "title": "Streams API", "desc": "Filter and map a collection." },
            { "title": "JDBC Intro", "desc": "Connect to a mock database." },
            { "title": "Linked List", "desc": "Implement a custom LinkedList class." },
            { "title": "Binary Tree", "desc": "Inorder traversal of a tree." }
          ]
        }
      ]
    },
    "4": { 
      "name": "Python",
      "sections": [
        {
          "section": "SECTION 1 — Basic",
          "problems": [
            { "title": "Print Statement", "desc": "Basic output using print().", "code": "print(\"Hello World\")", "output": "Hello World" },
            { "title": "Variables", "desc": "Dynamic typing in Python." },
            { "title": "Data Types", "desc": "Int, float, string, boolean." },
            { "title": "Input", "desc": "Read string and int using input()." },
            { "title": "Arithmetic", "desc": "Use +, -, *, /, //, **." },
            { "title": "String Slicing", "desc": "Extract substrings using [start:end]." },
            { "title": "List Basics", "desc": "Create and index lists." },
            { "title": "Tuple Basics", "desc": "Create immutable sequences." },
            { "title": "Dict Basics", "desc": "Key-value pair initialization." },
            { "title": "Type Conversion", "desc": "Convert str to int and vice versa." }
          ]
        },
        {
          "section": "SECTION 2 — Easy",
          "problems": [
            { "title": "If-Elif-Else", "desc": "Conditional branching." },
            { "title": "For Loop", "desc": "Iterate over a range or list." },
            { "title": "While Loop", "desc": "Loop until a condition is false." },
            { "title": "Functions", "desc": "Define functions using 'def'." },
            { "title": "Factorial", "desc": "Calculate factorial using loop." },
            { "title": "Fibonacci", "desc": "Generate fibonacci sequence." },
            { "title": "Prime Number", "desc": "Check for prime numbers." },
            { "title": "Reverse String", "desc": "Reverse using [::-1]." },
            { "title": "List Methods", "desc": "Append, remove, pop, sort." },
            { "title": "Dict Methods", "desc": "Get, keys, values, items." }
          ]
        },
        {
          "section": "SECTION 3 — Medium",
          "problems": [
            { "title": "List Comprehension", "desc": "Generate lists in one line." },
            { "title": "Lambda Functions", "desc": "Anonymous functions." },
            { "title": "Map & Filter", "desc": "Functional programming tools." },
            { "title": "*args and **kwargs", "desc": "Variable length arguments." },
            { "title": "Classes", "desc": "Define class and __init__." },
            { "title": "Objects", "desc": "Instantiate objects." },
            { "title": "Inheritance", "desc": "Class inheritance and super()." },
            { "title": "Polymorphism", "desc": "Method overriding." },
            { "title": "Exception Handling", "desc": "Try, except, finally." },
            { "title": "File I/O", "desc": "Read and write text files using 'with'." }
          ]
        },
        {
          "section": "SECTION 4 — Complex",
          "problems": [
            { "title": "Decorators", "desc": "Modify function behavior." },
            { "title": "Generators", "desc": "Yield keyword for lazy evaluation." },
            { "title": "Iterators", "desc": "Implement __iter__ and __next__." },
            { "title": "RegEx", "desc": "Pattern matching using 're' module." },
            { "title": "Threading", "desc": "Run tasks concurrently." },
            { "title": "Asyncio", "desc": "Asynchronous programming basics." },
            { "title": "Pandas Intro", "desc": "Dataframe creation and reading." },
            { "title": "Numpy Intro", "desc": "Array operations and broadcasting." },
            { "title": "Web Scraping", "desc": "Basic GET request using 'requests'." },
            { "title": "Binary Search Tree", "desc": "Implement insert and search in BST." }
          ]
        }
      ]
    }
  };

  const currentLanguage = allLanguageData[id];

  if (!currentLanguage) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f9fbff] p-10 font-dmsans pt-[100px]">
        <h2 className="text-xl font-outfit font-bold text-slate-800 mb-4">Language Not Found</h2>
        <p className="text-sm font-dmsans text-slate-500 mb-6">The language ID "{id}" does not exist in our records.</p>
        <Link to="/practice" className="px-6 py-2 bg-[#0284c7] text-white rounded-lg font-jakarta font-bold text-sm shadow-md hover:-translate-y-0.5 transition-all">
          Go Back to Practice
        </Link>
      </div>
    );
  }

  // ==========================================
  // VIEW 1: Split View (When a problem is clicked)
  // ==========================================
  if (selectedProblem) {
    let allProblems = [];
    currentLanguage.sections.forEach(sec => {
      sec.problems.forEach(prob => allProblems.push(prob));
    });
    
    const currentIndex = allProblems.findIndex(p => p.title === selectedProblem.title);
    const nextProblem = currentIndex >= 0 && currentIndex < allProblems.length - 1 ? allProblems[currentIndex + 1] : null;

    return (
      <div className="min-h-screen bg-[#f9fbff] pt-[100px] pb-12 px-[4%] lg:px-[8%] text-[#1e293b] selection:bg-[#00b4d8] selection:text-white font-sans flex flex-col lg:flex-row gap-6 items-start">
        
        {/* LEFT SIDEBAR (Problem List) */}
        <div className="w-full lg:w-[360px] shrink-0 bg-white border border-slate-200 rounded-[28px] shadow-sm flex flex-col h-auto lg:h-[calc(100vh-140px)] lg:sticky lg:top-[110px] overflow-hidden">
          
          <div className="p-6 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-[18px] font-outfit font-extrabold text-[#111827] mb-4 tracking-tight">
              {currentLanguage.name} Problems
            </h2>
            <div className="relative group">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-slate-400 group-focus-within:text-[#0088cc] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search..."
                className="font-jakarta w-full py-3 pl-10 pr-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/10 text-[13px] transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
            {currentLanguage.sections.map((section, sIndex) => {
              const filteredProblems = section.problems.filter(p => 
                p.title.toLowerCase().includes(searchTerm.toLowerCase())
              );
              if (filteredProblems.length === 0) return null;

              return (
                <div key={sIndex}>
                  <h3 className="text-[11px] font-mono font-bold text-slate-400 uppercase tracking-[0.15em] mb-3 px-2">
                    {section.section}
                  </h3>
                  <div className="space-y-2">
                    {filteredProblems.map((problem, pIndex) => {
                      const isActive = selectedProblem.title === problem.title;
                      return (
                        <div 
                          key={pIndex} 
                          onClick={() => setSelectedProblem(problem)}
                          className={`p-4 rounded-2xl transition-all duration-200 cursor-pointer border ${
                            isActive 
                              ? 'bg-[#f0f9ff] border-blue-200 shadow-sm' 
                              : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <h4 className={`text-[14px] font-jakarta font-bold mb-1 ${isActive ? 'text-[#0284c7]' : 'text-[#111827]'}`}>
                            {problem.title}
                          </h4>
                          <p className={`text-[12px] font-dmsans leading-relaxed line-clamp-2 ${isActive ? 'text-[#0284c7]/70 font-medium' : 'text-slate-500'}`}>
                            {problem.desc}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT MAIN CONTENT (Problem Details) */}
        <div className="flex-1 w-full bg-white border border-slate-200 rounded-[32px] shadow-sm p-6 md:p-10 lg:p-12 overflow-y-auto h-auto lg:h-[calc(100vh-140px)] lg:sticky lg:top-[110px] custom-scrollbar animate-in fade-in zoom-in-[0.98] duration-500 flex flex-col">
          
          <button 
            onClick={() => setSelectedProblem(null)} 
            className="lg:hidden mb-8 flex items-center gap-2 text-[#0284c7] font-jakarta font-bold text-[13px] bg-blue-50/80 px-5 py-2.5 rounded-full border border-blue-100 hover:bg-blue-100 transition-colors w-max"
          >
            &larr; Back to List
          </button>

          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-[12px] font-jakarta font-semibold text-slate-400 uppercase tracking-widest">
              <Link to="/practice" className="hover:text-[#0088cc] transition-colors flex items-center gap-1">
                <span>🏠</span> Practice
              </Link>
              <span className="text-slate-300">/</span>
              <button onClick={() => setSelectedProblem(null)} className="hover:text-[#0088cc] transition-colors">{currentLanguage.name}</button>
              <span className="text-slate-300">/</span>
              <span className="text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md truncate max-w-[200px]">{selectedProblem.title}</span>
            </nav>
          </div>

          <h1 className="text-[32px] md:text-[40px] font-outfit font-extrabold text-[#111827] mb-10 tracking-tight leading-tight">
            {selectedProblem.title}
          </h1>

          <div className="space-y-12 flex-grow">
            <section>
              <h2 className="text-[17px] font-jakarta font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-[20px]">📝</span> Problem Description
              </h2>
              <p className="text-[15px] font-dmsans text-slate-600 leading-relaxed font-medium">
                {selectedProblem.detailedDesc || `Write a program to solve the "${selectedProblem.title}" problem. Ensure your code handles all standard edge cases and follows the basic syntax rules of ${currentLanguage.name}.`}
              </p>
            </section>

            <section>
              <h2 className="text-[17px] font-jakarta font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-[20px]">🧠</span> Logic / Approach
              </h2>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm">
                <ul className="space-y-3 text-[14px] font-dmsans text-slate-700 leading-relaxed font-medium">
                  {(selectedProblem.logic || "1. Understand the problem requirements.\n2. Formulate the syntax and structure.\n3. Compile and verify the output.").split('\n').map((step, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500 shadow-sm mt-0.5">
                        {i + 1}
                      </span>
                      <span className="mt-0.5">{step.replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[17px] font-jakarta font-bold text-slate-900 flex items-center gap-2">
                  <span className="text-[20px]">💻</span> Solution Code
                </h2>
                <span className="text-[11px] font-mono font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-md uppercase tracking-widest">{currentLanguage.name}</span>
              </div>
              <div className="bg-[#0f172a] rounded-2xl p-6 overflow-x-auto shadow-xl relative group">
                <div className="flex gap-2 absolute top-4 left-5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                </div>
                <pre className="text-[14px] font-mono text-sky-300 leading-[1.7] mt-8">
                  <code>
                    {selectedProblem.code || `// Exact solution code for ${selectedProblem.title}\n// Will be updated dynamically.\n\nint main() {\n    // logic goes here\n    return 0;\n}`}
                  </code>
                </pre>
              </div>
            </section>

            <section>
              <h2 className="text-[17px] font-jakarta font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-[20px]">🖥️</span> Expected Output
              </h2>
              <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 shadow-inner">
                <pre className="text-[14px] font-mono text-emerald-400 font-medium">
                  {selectedProblem.output || "> Compilation Successful.\n> Expected output displayed."}
                </pre>
              </div>
            </section>
          </div>

          {/* NEXT PROBLEM BUTTON SECTION */}
          {nextProblem && (
            <div className="mt-16 pt-8 border-t border-slate-100 flex justify-end">
              <button 
                onClick={() => setSelectedProblem(nextProblem)}
                className="group flex flex-col items-end text-right outline-none"
              >
                <span className="text-[11px] font-jakarta font-bold text-slate-400 uppercase tracking-[0.2em] mb-2">
                  Next Problem
                </span>
                <div className="flex items-center gap-4 text-[#111827] group-hover:text-[#0284c7] transition-colors">
                  <span className="text-[18px] font-jakarta font-bold">
                    {nextProblem.title}
                  </span>
                  <div className="bg-slate-50 p-3 rounded-full group-hover:bg-blue-50 transition-colors border border-slate-200 group-hover:border-blue-200 shadow-sm">
                    <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform text-slate-400 group-hover:text-[#0284c7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          )}

        </div>
        
        <style>{`
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          
          /* Fonts */
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&family=Outfit:wght@100..900&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');
          .font-outfit { font-family: 'Outfit', sans-serif; }
          .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
          .font-dmsans { font-family: 'DM Sans', sans-serif; }
          .font-mono { font-family: 'JetBrains Mono', monospace; }
        `}</style>
      </div>
    );
  }

  // ==========================================
  // VIEW 2: Centered List (Default State before clicking)
  // ==========================================
  return (
    <div className="min-h-screen bg-[#f9fbff] px-[5%] md:px-[15%] lg:px-[20%] py-10 text-[#1e293b] pt-[120px] selection:bg-[#00b4d8] selection:text-white">
      
      <div className="w-full mb-6">
        <nav className="flex items-center space-x-2 text-[12px] font-jakarta font-medium text-slate-500 uppercase tracking-wider">
          <Link to="/practice" className="hover:text-[#0088cc] transition-colors flex items-center gap-1">
            <span>🏠</span> Practice
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-800 font-bold">{currentLanguage.name}</span>
        </nav>
      </div>

      <div className="w-full mb-10 text-left">
        <h1 className="text-[32px] md:text-[40px] font-outfit font-extrabold text-[#111827] mb-2 tracking-tight">
          {currentLanguage.name} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00b4d8] to-[#00c853]">Problems</span>
        </h1>
        <p className="text-[14px] font-dmsans text-gray-500 max-w-lg leading-relaxed font-medium">
          Select a problem to view the full description, logic breakdown, and optimal solution.
        </p>
      </div>

      <div className="w-full mb-14 flex justify-start">
        <div className="relative w-full rounded-2xl bg-gradient-to-r from-[#00b4d8] to-[#00c853] p-[2px] shadow-[0_10px_25px_-5px_rgba(0,180,216,0.15)]">
          <div className="relative bg-white rounded-[14px] group transition-all duration-300 overflow-hidden">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none z-10">
              <svg className="w-4.5 h-4.5 text-gray-400 group-focus-within:text-[#0088cc] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder={`Search in ${currentLanguage.name}...`}
              className="font-jakarta w-full py-3.5 pl-12 pr-4 bg-white text-gray-900 placeholder-gray-400 focus:outline-none text-[14px] transition-all font-medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="w-full space-y-12">
        {currentLanguage.sections.map((section, sIndex) => {
          const filteredProblems = section.problems.filter(p => 
            p.title.toLowerCase().includes(searchTerm.toLowerCase())
          );

          if (filteredProblems.length === 0) return null;

          return (
            <section key={sIndex} className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex items-center mb-5">
                <h2 className="text-[12px] font-mono font-bold text-[#64748b] uppercase tracking-[0.15em]">
                  {section.section}
                </h2>
                <div className="ml-4 flex-grow h-[1px] bg-gray-200/60"></div>
              </div>
              
              <div className="grid gap-3">
                {filteredProblems.map((problem, pIndex) => (
                  <div 
                    key={pIndex} 
                    onClick={() => setSelectedProblem(problem)} 
                    className="group flex items-center justify-between bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_-6px_rgba(0,180,216,0.2)] hover:border-blue-100 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                  >
                    <div>
                      <h3 className="text-[15px] font-jakarta font-bold text-[#111827] group-hover:text-[#0088cc] transition-colors mb-1">
                        {problem.title}
                      </h3>
                      <p className="text-[13px] font-dmsans text-gray-500 font-medium leading-relaxed">
                        {problem.desc}
                      </p>
                    </div>
                    
                    <div className="flex-shrink-0 bg-slate-50 group-hover:bg-[#f0f9ff] p-2.5 rounded-xl transition-colors ml-4 border border-transparent group-hover:border-blue-100">
                      <svg className="w-4 h-4 text-slate-300 group-hover:text-[#0088cc] transform group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&family=Outfit:wght@100..900&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');
        .font-outfit { font-family: 'Outfit', sans-serif; }
        .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
        .font-dmsans { font-family: 'DM Sans', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
      `}</style>
    </div>
  );
};

export default CProblems;